import BackgroundGeolocation, {
  type Subscription
} from "@transistorsoft/capacitor-background-geolocation"
import { Capacitor } from "@capacitor/core"
import { getToken } from "@clerk/vue"
import moment from "moment"

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000"
interface LocationRun {
  start: number
  expiresAt: number
  shouldTrack: () => boolean
  version: number
}

// One owner for the native plugin, shared by every import of this module.
let initialization: Promise<void> | undefined
let operations: Promise<void> = Promise.resolve()
let activeRun: LocationRun | null = null
let version = 0
let nativeStopped = false // Native tracking may have survived a previous app launch.
const subscriptions: Subscription[] = []

function enqueue(operation: () => Promise<void>) {
  const result = operations.then(operation)
  operations = result.catch(() => {})
  return result
}

async function getCurrentToken(): Promise<string> {
  try {
    return (await getToken({ template: "jwt" })) || ""
  } catch (error) {
    console.error("Failed to get Clerk token:", error)
    return ""
  }
}

function initialize(): Promise<void> {
  return (initialization ??= (async () => {
    const token = await getCurrentToken()
    await BackgroundGeolocation.ready({
      geolocation: {
        desiredAccuracy: BackgroundGeolocation.DesiredAccuracy.Low,
        activityType: BackgroundGeolocation.ActivityType.AutomotiveNavigation,
        allowIdenticalLocations: true,
        // Request "Always" permission for background location tracking
        locationAuthorizationRequest: "Always",
        locationAuthorizationAlert: {
          titleWhenNotEnabled: "Locatietoegang is vereist",
          titleWhenOff: "Locatietoegang is uitgeschakeld",
          instructions:
            "Je locatie delen zorgt voor betere planning en tevreden klanten. Privacy eerst: tracking is alleen actief tijdens je dienst.",
          cancelButton: "Annuleren",
          settingsButton: "Instellingen openen"
        }
      },
      app: {
        heartbeatInterval: 60,
        stopOnTerminate: false, // Continue tracking when app is terminated
        startOnBoot: true, // Auto-start on device reboot
        enableHeadless: true, // Enable headless mode for Android
        preventSuspend: true,
        backgroundPermissionRationale: {
          title: "{applicationName} toegang geven tot je locatie",
          message:
            "Om de planning goed te laten werken en klanten te informeren over hun bestelling, moet locatie delen op '{backgroundPermissionOptionLabel}' staan.",
          positiveAction: "Zet op '{backgroundPermissionOptionLabel}'",
          negativeAction: "Annuleren"
        },
        // Android foreground-service notification
        notification: {
          title: "Locatie wordt gedeeld",
          text: "Je locatie delen zorgt voor betere planning en tevreden klanten. Privacy eerst: tracking is alleen actief tijdens je dienst.",
          sticky: true // Notification persists until explicitly stopped
        }
      },
      http: {
        // HTTP Sync Configuration - Using POST (plugin doesn't support PATCH)
        // NOTE: Your backend endpoint /auth/current-user/location/ should accept POST
        url: `${API_URL}/auth/current-user/location/`,
        method: "POST",
        autoSync: true, // Automatically upload locations to server
        autoSyncThreshold: 0, // Upload immediately (no threshold)
        batchSync: false, // Send each location individually
        headers: {
          Authorization: token ? `Bearer ${token}` : ""
        }
      },
      persistence: {
        maxRecordsToPersist: 1 // Keep max 1 location in queue
      },
      logger: {
        debug: false,
        logLevel: BackgroundGeolocation.LogLevel.Verbose
      }
    })
  })().catch((error) => {
    initialization = undefined // A failed initialization must remain retryable.
    throw error
  }))
}

function isCurrent(run: LocationRun): boolean {
  return activeRun === run && run.version === version && run.shouldTrack()
}

function subscribe(run: LocationRun) {
  // Location and schedule events allow the lifecycle owner to enforce expiry after suspension.
  subscriptions.push(
    BackgroundGeolocation.onLocation(() => {
      isCurrent(run)
    })
  )
  subscriptions.push(
    BackgroundGeolocation.onSchedule(() => {
      isCurrent(run)
    })
  )
  subscriptions.push(
    BackgroundGeolocation.onHttp(() => {
      if (isCurrent(run)) {
        void updateAuthorizationToken().catch((error) =>
          console.warn("Failed to refresh location authorization:", error)
        )
      }
    })
  )
  subscriptions.push(
    BackgroundGeolocation.onHeartbeat(() => {
      if (!isCurrent(run)) return
      void BackgroundGeolocation.getCurrentPosition({ samples: 1, persist: true }).catch((error) =>
        console.error("Failed to request heartbeat location:", error)
      )
    })
  )
}

async function stopNative() {
  activeRun = null
  for (const subscription of subscriptions.splice(0)) {
    try {
      subscription.remove()
    } catch (error) {
      console.warn("Failed to remove location listener:", error)
    }
  }
  if (nativeStopped) return
  await initialize()
  // The scheduler must also stop, otherwise it can restart the native service.
  try {
    await BackgroundGeolocation.stopSchedule()
  } finally {
    await BackgroundGeolocation.stop()
  }
  nativeStopped = true // Only mark stopped after both calls succeed, so failures can be retried.
}

/** Start/update the single native run. Repeated calls do not duplicate subscriptions. */
export function trackLocation(
  start: number,
  expiresAt: number,
  shouldTrack: () => boolean
): Promise<void> {
  if (!Capacitor.isNativePlatform()) return Promise.resolve()
  const requestVersion = ++version
  return enqueue(async () => {
    if (requestVersion !== version) return
    if (!shouldTrack()) {
      await stopNative()
      return
    }
    if (activeRun?.start === start && activeRun.expiresAt === expiresAt) {
      activeRun.shouldTrack = shouldTrack
      activeRun.version = requestVersion
      return
    }
    await stopNative()
    if (requestVersion !== version || !shouldTrack()) return
    const run = { start, expiresAt, shouldTrack, version: requestVersion }
    activeRun = run
    nativeStopped = false
    try {
      const token = await getCurrentToken()
      if (!isCurrent(run)) {
        await stopNative()
        return
      }
      // ready() is called once; per-shift configuration is applied without reinitializing.
      await BackgroundGeolocation.setConfig({
        app: {
          // Native schedules are minute-granular and subject to OS wake-up delays.
          schedule: [
            `${moment(start).format("YYYY-MM-DD-HH:mm")} ${moment(expiresAt).format("YYYY-MM-DD-HH:mm")}`
          ]
        },
        http: { headers: { Authorization: token ? `Bearer ${token}` : "" } }
      })
      if (!isCurrent(run)) {
        await stopNative()
        return
      }
      subscribe(run)
      await BackgroundGeolocation.startSchedule()
      if (!isCurrent(run)) {
        await stopNative()
        return
      }
      await BackgroundGeolocation.start()
      if (!isCurrent(run)) await stopNative()
    } catch (error) {
      await stopNative()
      throw error
    }
  })
}

/** Invalidate pending starts immediately and clean up the shared native run. */
export function stopLocationTracking(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return Promise.resolve()
  version++
  return enqueue(stopNative)
}

export async function updateAuthorizationToken(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return
  await initialize()
  const token = await getCurrentToken()
  if (token)
    await BackgroundGeolocation.setConfig({
      http: { headers: { Authorization: `Bearer ${token}` } }
    })
}

export async function syncLocations(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return
  try {
    await updateAuthorizationToken()
    await BackgroundGeolocation.sync()
  } catch (error) {
    console.error("Manual location sync failed:", error)
  }
}

export async function getPendingCount(): Promise<number> {
  if (!Capacitor.isNativePlatform()) return 0
  try {
    return await BackgroundGeolocation.getCount()
  } catch (error) {
    console.error("Failed to get pending locations:", error)
    return 0
  }
}
